import { useEffect, useMemo, useState, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { buildCaseTaxonomyUrl } from "@/pages/Cases";
import {
  ScrollText, ChevronRight, Search, AlertTriangle, Loader2, Folder,
} from "lucide-react";


// LocalStorage keys per node — sidebar tree remembers user's expanded state.
// Post 2026-07-19 fix, keys are namespaced:  cat:<id> · svc:<id>
const STATE_KEY = "co-ops-cases-tree";


function loadOpenState() {
  try {
    return JSON.parse(window.localStorage.getItem(STATE_KEY) || "{}") || {};
  } catch (_) { return {}; }
}
function saveOpenState(s) {
  try { window.localStorage.setItem(STATE_KEY, JSON.stringify(s)); } catch (_) { /* localStorage disabled */ }
}


export default function OperationsCasesTree({ onNavigate, navigationVisible = true }) {
  const [tree, setTree] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(() => loadOpenState());
  const [search, setSearch] = useState("");
  const location = useLocation();
  const fetched = useRef(false);

  // Read query params so the active branch can auto-open. Sidebar is
  // now two-level: category → service → sub_type. Both `cat:` and `svc:`
  // keys are opened on deep-link.
  const qs = new URLSearchParams(location.search);
  const activeCat = qs.get("service_category_id") || "";
  const activeSvc = qs.get("service_id") || "";
  const activeSub = qs.get("service_sub_type_id") || "";

  useEffect(() => {
    if (!navigationVisible) return;
    const fetchTree = () => api.get("/navigation/operations-cases")
      .then((r) => setTree(r.data.data || { categories: [], totals: { all_cases: 0 } }))
      .catch((e) => setErr(e?.message || "Failed to load"));
    if (!fetched.current) { fetched.current = true; fetchTree(); }
    // Admin Settings → Workflows → Production Order dispatches this after
    // any save (custom name, reorder, toggles) so the persistent tree
    // never shows stale labels/order.
    window.addEventListener("certopz:ops-tree-refresh", fetchTree);
    return () => window.removeEventListener("certopz:ops-tree-refresh", fetchTree);
  }, [navigationVisible]);

  // Category expansion driven by deep-link + single-category auto-
  // expand. The service level was flattened in 2026-09-02, so we only
  // need to track `cat:<id>` keys now.  `activeSvc` and `activeSub`
  // both auto-open the parent category so the deep-linked leaf is
  // visible on first paint.
  useEffect(() => {
    if (!tree) return;
    setOpen((s) => {
      const next = { ...s };
      const cats = tree.categories || [];
      if (cats.length === 1 && next[`cat:${cats[0].id}`] === undefined) {
        next[`cat:${cats[0].id}`] = true;
      }
      if (activeCat) next[`cat:${activeCat}`] = true;
      if (activeSvc) {
        for (const cat of cats) {
          if ((cat.services || []).some((s) => s.id === activeSvc)) {
            next[`cat:${cat.id}`] = true;
          }
        }
      }
      if (activeSub) {
        for (const cat of cats) {
          for (const svc of cat.services || []) {
            if ((svc.sub_types || []).some((t) => t.id === activeSub)) {
              next[`cat:${cat.id}`] = true;
            }
          }
        }
      }
      saveOpenState(next);
      return next;
    });
  }, [tree, activeCat, activeSvc, activeSub]);

  const toggle = (key) => {
    setOpen((s) => {
      const next = { ...s, [key]: !s[key] };
      saveOpenState(next);
      return next;
    });
  };

  // Post 2026-07-19 audit fix — restore the two-level hierarchy the
  // backend already returns (category → service → sub_type). No
  // flattening. Categories are sorted by display_order then name;
  // services keep their intra-category order; sub_types keep theirs.
  // 2026-09-02 · Categories are now sorted purely alphabetically
  // (case-insensitive) — display_order is intentionally not used as
  // a primary sort key because most tenants left it at the default
  // 9999, which produced an unstable order across tenants.  Leaves
  // within a category are still sorted by display_order then name
  // (see `categories` memo below) so an admin can pin a canonical
  // leaf ordering per family when they need to.
  // 2026-09-07 · Production-Order alignment (Admin Settings → Workflows).
  // Two rules keep the sidebar in lock-step with the flat Production
  // Order strip:
  //   1. Services inside every category are ordered by their own
  //      `display_order` — driven by `POST /api/services/reorder`.
  //   2. Categories are ordered by the MIN service-display_order they
  //      contain, so if the admin drags a service to position 1, the
  //      category holding that service floats to the top of the tree
  //      too. Categories with no services fall through to their own
  //      display_order and finally their name so untenanted rows
  //      still have a deterministic slot.
  const catSortKey = (c) => (c.name || "").toLowerCase();
  const svcSortKey = (s) => [
    Number.isFinite(s.display_order) ? s.display_order : 9999,
    (s.name || "").toLowerCase(),
  ];

  const categories = useMemo(() => {
    if (!tree) return [];
    // Rule 2 — sort categories by the smallest service display_order
    // they contain; tie-break by category name (case-insensitive).
    const catProdOrderKey = (c) => {
      const svcs = c.services || [];
      let min = Infinity;
      for (const s of svcs) {
        const v = Number.isFinite(s.display_order) ? s.display_order : 9999;
        if (v < min) min = v;
      }
      return min === Infinity ? 9999 : min;
    };
    const cats = (tree.categories || []).slice().sort((a, b) => {
      const ao = catProdOrderKey(a);
      const bo = catProdOrderKey(b);
      if (ao !== bo) return ao - bo;
      return catSortKey(a).localeCompare(catSortKey(b));
    });
    // 2026-09-02 · Flatten the middle "service" level.
    // On tenants where a category has one or more services that all
    // carry the SAME set of sub-types (legacy seed drift, e.g. the
    // "ISO Certifications" category on Sigma Global Services holds
    // six leaf services `ISO 9001`, `ISO 14001`, … each linked to
    // the identical eight canonical sub-types), the sidebar rendered
    // a redundant intermediate node:
    //     ISO Certifications › ISO 9001 › [ISO 9001, ISO 14001, …]
    // Users expect: `Category › [dedup'd sub-types]`.  We now merge
    // every service's sub-types into a single `__leaves` array on
    // the category, deduped by lowercased name so `service +
    // subcategory` becomes unique across the tree.  A service that
    // has no sub-types is promoted to a leaf itself, so services
    // that are meaningful in their own right (rare on Sigma but
    // possible on other tenants) still render as clickable leaves.
    return cats.map((c) => {
      const bySlug = new Map(); // slug → { name, id, case_count, __isSub, __display_order }
      const services = (c.services || []).slice().sort(
        (a, b) => {
          const [ao, an] = svcSortKey(a); const [bo, bn] = svcSortKey(b);
          return ao - bo || an.localeCompare(bn);
        },
      );
      // 2026-09-07 · Root-cause fix — leaves now inherit the PARENT
      // service's display_order (Production Order) as their sort key,
      // not the sub-type's own display_order. Without this, a service
      // pinned to position #10 in Production Order but whose sub-types
      // each carry display_order 1..N would float to the TOP of its
      // category — divorcing the sidebar from the strip. Sub-types of
      // one service still keep their internal order via the outer
      // `services` iteration, and the final sort on __display_order
      // is now a no-op tie-breaker (Map insertion order is preserved).
      for (const svc of services) {
        const subs = svc.sub_types || [];
        const parentOrder = Number.isFinite(svc.display_order) ? svc.display_order : 9999;
        if (subs.length === 0) {
          const slug = `svc:${(svc.name || "").toLowerCase().trim()}`;
          const prior = bySlug.get(slug);
          if (prior) {
            prior.case_count = (prior.case_count || 0) + (svc.case_count || 0);
          } else {
            bySlug.set(slug, {
              id: svc.id,
              name: svc.name,
              code: svc.code,
              case_count: svc.case_count || 0,
              __isSub: false,
              __display_order: parentOrder,
            });
          }
          continue;
        }
        // Sub-types inherit the parent service's Production-Order slot
        // and are ordered internally by their own display_order (kept
        // in a fresh copy so we don't mutate the source array).
        const orderedSubs = subs.slice().sort((a, b) => {
          const ao = Number.isFinite(a.display_order) ? a.display_order : 9999;
          const bo = Number.isFinite(b.display_order) ? b.display_order : 9999;
          return ao - bo || (a.name || "").localeCompare(b.name || "");
        });
        for (const sub of orderedSubs) {
          const slug = `sub:${(sub.name || "").toLowerCase().trim()}`;
          const prior = bySlug.get(slug);
          if (prior) {
            prior.case_count = (prior.case_count || 0) + (sub.case_count || 0);
          } else {
            bySlug.set(slug, {
              id: sub.id,
              name: sub.name,
              code: sub.code,
              case_count: sub.case_count || 0,
              __isSub: true,
              __display_order: parentOrder,
            });
          }
        }
      }
      // Preserve Map insertion order (already Production-Order-driven);
      // tie-break only on identical __display_order to keep the sort
      // deterministic across React renders.
      const leaves = [...bySlug.values()];
      // 2026-09-08 · Custom product description. The service level is
      // flattened away, so a service's custom `display_name` would never
      // be visible. When the category is a 1:1 wrapper around a single
      // workflow (the canonical catalogue shape) and that workflow has a
      // custom name, the group header shows the custom name; otherwise
      // the standard category name stands.
      const only = services.length === 1 ? services[0] : null;
      const customLabel = only && only.display_name ? only.display_name : null;
      return { ...c, name: customLabel || c.name, __canonical_name: c.name, services, __leaves: leaves };
    });
  }, [tree]);

  // Search filters keep matching leaves + their parent category
  // visible. A category is visible when it or any of its flattened
  // leaves match. Matched categories auto-open during a search so the
  // result is directly clickable.
  const visibleCategories = useMemo(() => {
    if (!search.trim()) return categories;
    const q = search.trim().toLowerCase();
    const m = (s) => (s || "").toLowerCase().includes(q);
    const out = [];
    for (const cat of categories) {
      const catHit = m(cat.name) || m(cat.code);
      const matchedLeaves = (cat.__leaves || []).filter((l) =>
        catHit || m(l.name) || m(l.code));
      if (matchedLeaves.length || catHit) {
        out.push({ ...cat, __leaves: matchedLeaves });
      }
    }
    return out;
  }, [categories, search]);

  // Total leaf count decides whether to show the filter box.
  const totalLeafCount = useMemo(
    () => categories.reduce((n, c) => n + (c.__leaves?.length || 0), 0),
    [categories]);
  const showSearch = totalLeafCount > 12;
  if (!navigationVisible) return null;

  return (
    <div data-testid="ops-cases-tree" className="space-y-0.5">
      <NavLink
        to={buildCaseTaxonomyUrl({}, location.search)} end onClick={onNavigate}
        className={({ isActive }) =>
          `co-sidebar-link co-sidebar-child ${(isActive && !activeCat && !activeSvc && !activeSub) ? "active" : ""}`}
        data-testid="nav-cases">
        <ScrollText className="w-4 h-4" />
        <span className="flex-1">All Cases</span>
        {tree ? (
          <span className="text-[10px] text-slate-400 font-mono"
                 data-testid="ops-cases-all-count">{tree.totals?.all_cases ?? 0}</span>
        ) : null}
      </NavLink>

      {err ? (
        <div className="px-3 py-2 text-[11px] text-amber-300 flex items-center gap-1"
             data-testid="ops-cases-tree-error">
          <AlertTriangle className="w-3 h-3" />Tree unavailable
        </div>
      ) : !tree ? (
        <div className="px-3 py-2 text-[11px] text-slate-500 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />Loading…
        </div>
      ) : (
        <>
          {showSearch ? (
            <div className="px-2 py-1.5">
              <div className="relative">
                <Search className="w-3 h-3 absolute left-2 top-1.5 text-slate-400" />
                <input
                  data-testid="ops-cases-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filter services / subcategories…"
                  className="w-full text-[11px] pl-6 pr-2 py-1 rounded border border-slate-600 bg-slate-800/40 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                />
              </div>
            </div>
          ) : null}

          {visibleCategories.length === 0 ? (
            <div className="px-3 py-1.5 text-[11px] text-slate-500" data-testid="ops-cases-empty">
              No services yet.
            </div>
          ) : null}

          {visibleCategories.map((cat) => {
            // Open when: user toggled it, deep-linked into it, or during
            // an active search (matched leaves within must be visible).
            const isOpen = search.trim()
              ? true
              : !!open[`cat:${cat.id}`];
            return (
              <CategoryNode
                key={cat.id} cat={cat} isOpen={isOpen}
                onToggle={() => toggle(`cat:${cat.id}`)}
                activeCat={activeCat}
                activeSvc={activeSvc}
                activeSub={activeSub}
                onNavigate={onNavigate}
                currentSearch={location.search}
              />
            );
          })}
        </>
      )}
    </div>
  );
}


function CategoryNode({
  cat, isOpen, onToggle,
  activeCat, activeSvc, activeSub, onNavigate, currentSearch,
}) {
  const isVirtual = typeof cat.id === "string" && cat.id.startsWith("svc-group:");
  const groupServiceId = isVirtual ? cat.id.slice("svc-group:".length) : null;
  const isActive = isVirtual
    ? activeSvc === groupServiceId && !activeSub
    : activeCat === cat.id && !activeSvc && !activeSub;
  return (
    <div data-testid={`ops-cat-${cat.id}`}>
      <div className="flex items-center">
        <button
          type="button"
          className="p-1 text-slate-400 hover:text-slate-200"
          onClick={onToggle}
          aria-expanded={isOpen}
          data-testid={`ops-cat-toggle-${cat.code || cat.id}`}
        >
          <ChevronRight className={`w-3 h-3 transition-transform ${isOpen ? "rotate-90" : ""}`} />
        </button>
        <NavLink
          to={isVirtual
            ? buildCaseTaxonomyUrl({ serviceId: groupServiceId }, currentSearch)
            : buildCaseTaxonomyUrl({ categoryId: cat.id }, currentSearch)}
          onClick={onNavigate}
          className={`co-sidebar-link co-sidebar-child flex-1 font-medium ${isActive ? "active" : ""}`}
          data-testid={`ops-cat-link-${cat.code || cat.id}`}
        >
          <Folder className="w-3.5 h-3.5 text-slate-400" />
          <span className="flex-1 truncate" title={cat.name}>{cat.name}</span>
          <span className="text-[10px] text-slate-400 font-mono">{cat.case_count ?? 0}</span>
        </NavLink>
      </div>

      {isOpen ? (
        <div className="ml-4 mt-0.5 space-y-0.5 border-l border-slate-700 pl-1">
          {(cat.__leaves || []).map((leaf) => {
            const isSubActive = leaf.__isSub && activeSub === leaf.id;
            const isSvcActive = !leaf.__isSub && activeSvc === leaf.id && !activeSub;
            const url = leaf.__isSub
              ? buildCaseTaxonomyUrl({ subTypeId: leaf.id }, currentSearch)
              : buildCaseTaxonomyUrl({ serviceId: leaf.id }, currentSearch);
            const testid = leaf.__isSub
              ? `ops-sub-link-${leaf.id}`
              : `ops-svc-link-${leaf.id}`;
            return (
              <div className="flex items-center" key={`${leaf.__isSub ? "sub" : "svc"}:${leaf.id}`}>
                <span className="p-1 invisible"><ChevronRight className="w-3 h-3" /></span>
                <NavLink
                  to={url}
                  onClick={onNavigate}
                  className={`co-sidebar-link co-sidebar-child flex-1 ${(isSubActive || isSvcActive) ? "active" : ""}`}
                  data-testid={testid}
                >
                  <span className="flex-1 truncate" title={leaf.name}>{leaf.name}</span>
                  <span className="text-[10px] text-slate-400 font-mono">{leaf.case_count ?? 0}</span>
                </NavLink>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}



